/**
 * Visually highlight a registered UI element by its `data-help-id`.
 * Used by the Help chatbot's "Show me" action.
 *
 * Implemented as a separate, position:fixed overlay element appended to <body>
 * rather than a class on the target. This survives React re-renders of the
 * target (e.g. when the destination page finishes loading its data and rewrites
 * the element's className), which would otherwise strip a class-based highlight.
 */

const OVERLAY_ID = 'ai-help-highlight-overlay';
const DURATION_MS = 6000;

function removeOverlay() {
  document.getElementById(OVERLAY_ID)?.remove();
}

export function highlightHelpElement(helpId: string): boolean {
  const el = document.querySelector<HTMLElement>(`[data-help-id="${helpId}"]`);
  if (!el) return false;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  removeOverlay();

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'ai-highlight-overlay';
  // Match the active portal accent colour.
  const accent = getComputedStyle(el).getPropertyValue('--accent-rgb').trim();
  if (accent) overlay.style.setProperty('--accent-rgb', accent);
  document.body.appendChild(overlay);

  const position = () => {
    const r = el.getBoundingClientRect();
    overlay.style.top = `${r.top - 5}px`;
    overlay.style.left = `${r.left - 5}px`;
    overlay.style.width = `${r.width + 10}px`;
    overlay.style.height = `${r.height + 10}px`;
  };
  position();

  // Keep the ring aligned during the smooth scroll and any layout shifts.
  const onMove = () => position();
  window.addEventListener('scroll', onMove, true);
  window.addEventListener('resize', onMove);
  let ticks = 0;
  const reposition = window.setInterval(() => {
    position();
    if (++ticks > 12) window.clearInterval(reposition);
  }, 100);

  window.setTimeout(() => {
    window.clearInterval(reposition);
    window.removeEventListener('scroll', onMove, true);
    window.removeEventListener('resize', onMove);
    overlay.remove();
  }, DURATION_MS);

  return true;
}

/**
 * Try to highlight now; if the element isn't on the current page, navigate to
 * `fallbackUrl` first and poll for the element to appear, then highlight.
 * Returns true if a highlight (or navigation+highlight) was initiated.
 */
export function highlightOrNavigate(
  helpId: string | null | undefined,
  fallbackUrl: string | null | undefined,
  navigate: (url: string) => void
): boolean {
  if (helpId && highlightHelpElement(helpId)) return true;
  if (!fallbackUrl) return false;

  navigate(fallbackUrl);
  if (!helpId) return true;

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (highlightHelpElement(helpId) || attempts > 12) {
      window.clearInterval(timer);
    }
  }, 250);
  return true;
}
